// Minimal JPEG EXIF reader: GPS position and the original capture time.
//
// Uploads from the gallery have no live GPS fix, so the contract's
// `capturedAt` and lat/lng come from the file's own metadata instead. When
// the file carries neither, the caller must let the user fill them in by
// hand: an entry is never silently dropped and never silently stamped with
// "now" as if it were real.

export interface ExifResult {
  lat: number | null;
  lng: number | null;
  capturedAt: string | null;
}

const EMPTY: ExifResult = { lat: null, lng: null, capturedAt: null };

const TYPE_SIZES: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1, 9: 4, 10: 8 };

interface Reader {
  view: DataView;
  tiff: number;
  little: boolean;
}

function readValue(reader: Reader, type: number, count: number, offset: number): number[] | string {
  const { view, little } = reader;
  if (type === 2) {
    let out = "";
    for (let i = 0; i < count; i += 1) {
      const code = view.getUint8(offset + i);
      if (code === 0) break;
      out += String.fromCharCode(code);
    }
    return out;
  }
  const values: number[] = [];
  for (let i = 0; i < count; i += 1) {
    if (type === 3) values.push(view.getUint16(offset + i * 2, little));
    else if (type === 4) values.push(view.getUint32(offset + i * 4, little));
    else if (type === 5) {
      const numerator = view.getUint32(offset + i * 8, little);
      const denominator = view.getUint32(offset + i * 8 + 4, little);
      values.push(denominator === 0 ? 0 : numerator / denominator);
    } else if (type === 9) values.push(view.getInt32(offset + i * 4, little));
    else if (type === 10) {
      const numerator = view.getInt32(offset + i * 8, little);
      const denominator = view.getInt32(offset + i * 8 + 4, little);
      values.push(denominator === 0 ? 0 : numerator / denominator);
    } else values.push(view.getUint8(offset + i));
  }
  return values;
}

function readIfd(reader: Reader, ifdOffset: number): Map<number, number[] | string> {
  const { view, tiff, little } = reader;
  const entries = new Map<number, number[] | string>();
  const base = tiff + ifdOffset;
  if (base + 2 > view.byteLength) return entries;
  const count = view.getUint16(base, little);
  for (let i = 0; i < count; i += 1) {
    const entry = base + 2 + i * 12;
    if (entry + 12 > view.byteLength) break;
    const tag = view.getUint16(entry, little);
    const type = view.getUint16(entry + 2, little);
    const length = view.getUint32(entry + 4, little);
    const size = (TYPE_SIZES[type] ?? 1) * length;
    const valueOffset = size > 4 ? tiff + view.getUint32(entry + 8, little) : entry + 8;
    if (valueOffset + size > view.byteLength) continue;
    entries.set(tag, readValue(reader, type, length, valueOffset));
  }
  return entries;
}

function toDegrees(parts: number[] | string | undefined, ref: number[] | string | undefined) {
  if (!Array.isArray(parts) || parts.length < 3) return null;
  const [deg = 0, min = 0, sec = 0] = parts;
  let value = deg + min / 60 + sec / 3600;
  const direction = typeof ref === "string" ? ref.trim().toUpperCase() : "";
  if (direction === "S" || direction === "W") value = -value;
  return Number.isFinite(value) ? value : null;
}

/**
 * "2026:08:24 14:05:33" is the EXIF format, local time with no zone. A few
 * cameras and editing tools write ISO style dashes instead, so both
 * separators are accepted: a wrong date on a diary entry is worse than a
 * slightly liberal parser.
 */
function toIso(raw: number[] | string | undefined): string | null {
  if (typeof raw !== "string") return null;
  const match = /^(\d{4})[:-](\d{2})[:-](\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(raw.trim());
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function parseExifBuffer(buffer: ArrayBuffer): ExifResult {
  const view = new DataView(buffer);
  if (view.byteLength < 12 || view.getUint16(0) !== 0xffd8) return EMPTY;

  let offset = 2;
  let app1 = -1;
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) break;
    const marker = view.getUint8(offset + 1);
    if (marker === 0xd8 || marker === 0xd9) break;
    const length = view.getUint16(offset + 2);
    if (marker === 0xe1) {
      app1 = offset + 4;
      break;
    }
    offset += 2 + length;
  }
  if (app1 < 0 || app1 + 6 > view.byteLength) return EMPTY;

  let exifHeader = "";
  for (let i = 0; i < 4; i += 1) exifHeader += String.fromCharCode(view.getUint8(app1 + i));
  if (exifHeader !== "Exif") return EMPTY;

  const tiff = app1 + 6;
  if (tiff + 8 > view.byteLength) return EMPTY;
  const byteOrder = view.getUint16(tiff);
  if (byteOrder !== 0x4949 && byteOrder !== 0x4d4d) return EMPTY;
  const little = byteOrder === 0x4949;
  const reader: Reader = { view, tiff, little };

  const ifd0 = readIfd(reader, view.getUint32(tiff + 4, little));

  let capturedAt: string | null = null;
  const exifPointer = ifd0.get(0x8769);
  if (Array.isArray(exifPointer) && exifPointer[0] !== undefined) {
    const exifIfd = readIfd(reader, exifPointer[0]);
    capturedAt = toIso(exifIfd.get(0x9003)) ?? toIso(exifIfd.get(0x9004));
  }
  if (!capturedAt) capturedAt = toIso(ifd0.get(0x0132));

  let lat: number | null = null;
  let lng: number | null = null;
  const gpsPointer = ifd0.get(0x8825);
  if (Array.isArray(gpsPointer) && gpsPointer[0] !== undefined) {
    const gps = readIfd(reader, gpsPointer[0]);
    lat = toDegrees(gps.get(2), gps.get(1));
    lng = toDegrees(gps.get(4), gps.get(3));
    if (lat !== null && (lat < -90 || lat > 90)) lat = null;
    if (lng !== null && (lng < -180 || lng > 180)) lng = null;
  }

  return { lat, lng, capturedAt };
}

export async function readExif(file: Blob): Promise<ExifResult> {
  if (!file.type.startsWith("image/jpeg")) return EMPTY;
  try {
    // The metadata block lives in the first few kilobytes, never read the
    // whole photo into memory on a phone just to find it.
    const head = file.slice(0, 256 * 1024);
    return parseExifBuffer(await head.arrayBuffer());
  } catch {
    return EMPTY;
  }
}
